import React, { useState, useRef, useCallback } from 'react';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import useInquiries from '../hooks/useInquiries';
import InquiryActions from '../components/inquiries/InquiryActions';
import InquiriesTable from '../components/inquiries/InquiriesTable';
import InquiryForm from '../components/inquiries/InquiryForm';
import InquiryDetails from '../components/inquiries/InquiryDetails';
import ConfirmDialog from '../components/common/ConfirmDialog';
import MessageAlert from '../components/common/MessageAlert';
import { useErrorDialog } from '../context/errorDialogContext';
import { useWarehouseContext } from '../context/WarehouseContext';
import inquiriesService from '../services/inquiriesService';
import Pagination from '../components/common/Pagination';
import { getDefaultDateRange } from '../utils/format';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'inquiries_page_size';

const InquiriesPage = () => {
  const { t } = useTranslation();
  const {
    inquiries,
    loading,
    error,
    clearError,
    totalCount,
    currentPage,
    totalPages,
    fetchInquiries,
    createInquiry,
    updateInquiry,
    updateInquiryStatus,
    deleteInquiry,
    bulkDeleteInquiries,
    sendQuoteNotification,
    updateFilters,
    goToPage,
    setPageSize: setHookPageSize,
  } = useInquiries();

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
  });

  // Sync initial page size to hook
  React.useEffect(() => {
    setHookPageSize(pageSize);
  }, [pageSize, setHookPageSize]);

  const [selectedInquiries, setSelectedInquiries] = useState([]);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [editingInquiryId, setEditingInquiryId] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(new Set());
  const [localInquiryUpdates, setLocalInquiryUpdates] = useState(new Map()); // Track local status updates
  const [localDeletes, setLocalDeletes] = useState(new Set()); // Track local deletions
  const [viewingInquiry, setViewingInquiry] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const searchDebounceRef = useRef(null);
  const [searchInputValue, setSearchInputValue] = useState('');

  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
  const { from_date: fromDate, to_date: toDate } = dateRange;

  const handleFromDateChange = (val) => {
    setDateRange(prev => ({ ...prev, from_date: val }));
    updateFilters({ from_date: val });
  };

  const handleToDateChange = (val) => {
    setDateRange(prev => ({ ...prev, to_date: val }));
    updateFilters({ to_date: val });
  };

  const handleSearch = useCallback((searchTerm) => {
    setSearchInputValue(searchTerm);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      updateFilters({ search: searchTerm });
    }, 400);
  }, [updateFilters]);

  const strings = t('inquiriesModule.inquiries', { returnObjects: true });
  const { showError } = useErrorDialog();
  const { selectedWarehouseIds } = useWarehouseContext();

  React.useEffect(() => {
    if (error) {
      showError(error, strings.page?.alerts?.errorTitle);
      // Clear once shown: `error` sticks until the next request, and this effect re-runs on
      // every render because `strings.page` is a fresh object each time (t(..., { returnObjects:
      // true }) does not memoize). Leaving it set re-opens the dialog the operator just
      // dismissed, which reads as a frozen screen.
      clearError();
    }
  }, [error, showError, clearError, strings.page]);

  React.useEffect(() => {
    fetchInquiries({
      page: currentPage,
      warehouse_ids: selectedWarehouseIds,
      from_date: fromDate,
      to_date: toDate,
    });
  }, [fetchInquiries, currentPage, selectedWarehouseIds, fromDate, toDate]);

  const filteredInquiries = React.useMemo(() => {
    let baseInquiries = inquiries;

    // Filter out locally deleted items
    if (localDeletes.size > 0) {
      baseInquiries = baseInquiries.filter(i => !localDeletes.has(i.id));
    }

    // Apply local status updates that haven't been reflected in the main state yet
    if (localInquiryUpdates.size > 0) {
      baseInquiries = baseInquiries.map(inquiry => {
        const localUpdate = localInquiryUpdates.get(inquiry.id);
        return localUpdate ? { ...inquiry, ...localUpdate } : inquiry;
      });
    }

    return baseInquiries;
  }, [inquiries, localInquiryUpdates, localDeletes]);

  const selectInquiry = (id, checked) => {
    setSelectedInquiries(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  };
  const selectAllInquiries = (checked) => {
    if (checked) setSelectedInquiries(filteredInquiries.map(i => i.id));
    else setSelectedInquiries([]);
  };

  const hasSelection = selectedInquiries.length > 0;
  const selectionCount = selectedInquiries.length;

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      await fetchInquiries({ force: true, from_date: fromDate, to_date: toDate });
    } catch (err) {
      console.error('Failed to refresh inquiries', err);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setHookPageSize(newSize);
    updateFilters({ limit: newSize }); // Trigger refresh via hook's filters/currentPage change
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
  };

  const handleCreateInquiry = () => {
    setEditingInquiry(null);
    setEditingInquiryId(null);
    setShowInquiryForm(true);
  };

  const handleEditInquiry = async (inquiryId) => {
    setEditingInquiry(null); // Clear any existing inquiry data
    setEditingInquiryId(inquiryId); // Set the ID for the form to fetch
    setIsConverting(false);
    setShowInquiryForm(true); // Open form immediately
    // Form will fetch the inquiry data internally using inquiryId
  };

  const handleConvertInquiry = async (inquiryId) => {
    setEditingInquiry(null);
    setEditingInquiryId(inquiryId);
    setIsConverting(true);
    setShowInquiryForm(true);
  };

  const handleViewInquiry = async (inquiryId) => {
    setViewLoading(true);
    setViewingInquiry(null);
    setShowViewModal(true);
    try {
      const result = await inquiriesService.getInquiryById(inquiryId, { force: true });
      if (result.success && result.data) setViewingInquiry(result.data);
      else showError(result.error || t('inquiries.errors.failedToLoad'), strings.page?.alerts?.errorTitle);
    } catch (err) {
      console.error('View inquiry failed', err);
      showError(err.message || t('inquiries.errors.failedToLoad'), strings.page?.alerts?.errorTitle);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDeleteInquiry = (inquiryId) => {
    const inquiry = (filteredInquiries || []).find(i => i.id === inquiryId) || null;
    const displayName = inquiry?.customer_name || strings.table?.copy?.noCustomer;
    setDeleteConfirm({
      type: 'single',
      inquiry,
      inquiryId,
      title: strings.table?.actions?.delete || strings.table?.columns?.actions,
      message: `${strings.table?.columns?.customerName || 'Customer'}: ${displayName}`,
      confirmLabel: t('common.dialogs.delete.confirm'),
      cancelLabel: t('common.dialogs.delete.cancel'),
      confirmVariant: 'danger',
    });
  };

  const handleBulkDelete = () => {
    if (selectedInquiries.length === 0) return;
    setDeleteConfirm({
      type: 'bulk',
      title: `Delete ${selectedInquiries.length} Inquiries`,
      message: `Are you sure you want to delete ${selectedInquiries.length} selected inquiries? This action cannot be undone.`,
      confirmLabel: t('common.dialogs.delete.confirm'),
      cancelLabel: t('common.dialogs.delete.cancel'),
      confirmVariant: 'danger',
    });
  };

  const handleApproveInquiry = async (inquiryId) => {
    // Immediately update local state
    setLocalInquiryUpdates(prev => new Map(prev).set(inquiryId, { status: 'approved' }));
    setStatusLoading(prev => new Set([...prev, inquiryId]));

    try {
      const result = await updateInquiryStatus(inquiryId, 'approved');
      if (result.success) {
        setAlertMessage({
          type: 'success',
          title: t('common.status.success'),
          message: t('inquiriesModule.inquiries.notifications.approved', { id: String(inquiryId) })
        });
      } else {
        // Revert local update on failure
        setLocalInquiryUpdates(prev => {
          const newMap = new Map(prev);
          newMap.delete(inquiryId);
          return newMap;
        });
        showError(result.error || t('inquiries.errors.failedToApprove'), t('inquiries.errors.approvalErrorTitle'));
      }
    } catch (err) {
      console.error('Approve inquiry failed', err);
      // Revert local update on error
      setLocalInquiryUpdates(prev => {
        const newMap = new Map(prev);
        newMap.delete(inquiryId);
        return newMap;
      });
      showError(err.message || t('inquiries.errors.failedToApprove'), t('inquiries.errors.approvalErrorTitle'));
    } finally {
      setStatusLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(inquiryId);
        return newSet;
      });
    }
  };

  const handleRejectInquiry = async (inquiryId) => {
    // Immediately update local state
    setLocalInquiryUpdates(prev => new Map(prev).set(inquiryId, { status: 'rejected' }));
    setStatusLoading(prev => new Set([...prev, inquiryId]));

    try {
      const result = await updateInquiryStatus(inquiryId, 'rejected');
      if (result.success) {
        setAlertMessage({
          type: 'success',
          title: t('common.status.success'),
          message: t('inquiriesModule.inquiries.notifications.rejected', { id: String(inquiryId) })
        });
      } else {
        // Revert local update on failure
        setLocalInquiryUpdates(prev => {
          const newMap = new Map(prev);
          newMap.delete(inquiryId);
          return newMap;
        });
        showError(result.error || t('inquiries.errors.failedToReject'), t('inquiries.errors.rejectionErrorTitle'));
      }
    } catch (err) {
      console.error('Reject inquiry failed', err);
      // Revert local update on error
      setLocalInquiryUpdates(prev => {
        const newMap = new Map(prev);
        newMap.delete(inquiryId);
        return newMap;
      });
      showError(err.message || t('inquiries.errors.failedToReject'), t('inquiries.errors.rejectionErrorTitle'));
    } finally {
      setStatusLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(inquiryId);
        return newSet;
      });
    }
  };

  const confirmDelete = async () => {
    setActionLoading(true);
    let idsToDelete = [];
    if (deleteConfirm.type === 'single') {
      const id = deleteConfirm.inquiryId || deleteConfirm.inquiry?.id;
      if (id) idsToDelete.push(id);
    } else {
      idsToDelete = selectedInquiries || [];
    }

    // Optimistically delete locally
    setLocalDeletes(prev => {
      const newSet = new Set(prev);
      idsToDelete.forEach(id => newSet.add(id));
      return newSet;
    });

    try {
      let result;
      if (deleteConfirm.type === 'single') {
        const id = deleteConfirm.inquiryId || deleteConfirm.inquiry?.id;
        result = await deleteInquiry(id);
      } else {
        result = await bulkDeleteInquiries(selectedInquiries || []);
      }

      if (result.success) {
        setDeleteConfirm(null);
        setSelectedInquiries([]);
      } else {
        // Revert local delete on failure
        setLocalDeletes(prev => {
          const newSet = new Set(prev);
          idsToDelete.forEach(id => newSet.delete(id));
          return newSet;
        });
        showError(result.error || t('inquiries.errors.deleteFailed'), t('inquiries.errors.deletionErrorTitle'));
      }
    } catch (err) {
      console.error('Delete failed', err);
      // Revert local delete on error
      setLocalDeletes(prev => {
        const newSet = new Set(prev);
        idsToDelete.forEach(id => newSet.delete(id));
        return newSet;
      });
      showError(err.message || t('inquiries.errors.deleteFailed'), t('inquiries.errors.deletionErrorTitle'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleFormSubmit = async (payload) => {
    setActionLoading(true);
    try {
      let result;
      // If we have an editing inquiry object or an editing id, treat this as an update
      if (editingInquiry) {
        // Optimistically update
        setLocalInquiryUpdates(prev => new Map(prev).set(editingInquiry.id, { ...payload }));
        result = await updateInquiry(editingInquiry.id, payload);
      } else if (editingInquiryId) {
        // Optimistically update
        setLocalInquiryUpdates(prev => new Map(prev).set(editingInquiryId, { ...payload }));
        result = await updateInquiry(editingInquiryId, payload);
        // If this was a convert action and update was successful, proceed to approve
        if (result.success && isConverting) {
          const approvalPayload = {
            status: 'approved',
            source_type: payload.source_type,
            warehouse_id: payload.warehouse_id,
            supplier_id: payload.supplier_id,
            loading_cost_per_kg: payload.loading_cost_per_kg,
          };

          const approveResult = await updateInquiryStatus(editingInquiryId, approvalPayload);
          if (approveResult.success) {
            setAlertMessage({
              type: 'success',
              title: t('common.status.success'),
              message: t('inquiries.notifications.converted')
            });
            setLocalInquiryUpdates(prev => new Map(prev).set(editingInquiryId, { ...payload, status: 'approved' }));
            setShowInquiryForm(false);
            setEditingInquiry(null);
            setEditingInquiryId(null);
            setIsConverting(false);
            return approveResult;
          } else {
            showError(approveResult.error || t('inquiries.errors.failedToConvert'), t('inquiries.errors.conversionErrorTitle'));
            return approveResult;
          }
        }
      } else {
        result = await createInquiry(payload);
        // For new inquiries, especially with new customers, refresh the list to get proper data structure
        if (result.success) {
          await fetchInquiries({ force: true });
        }
      }
      if (result.success) {
        setShowInquiryForm(false);
        setEditingInquiry(null);
        setEditingInquiryId(null);
        setIsConverting(false);
      } else {
        showError(result.error || t('inquiries.errors.operationFailed'), t('inquiries.errors.formErrorTitle'));
      }
      return result;
    } catch (err) {
      console.error('Form submit failed', err);
      showError(err.message || t('inquiries.errors.operationFailed'), t('inquiries.errors.formErrorTitle'));
      return { success: false, error: err.message };
    } finally {
      setActionLoading(false);
    }
  };

  const countInquiries = () => {
    if (typeof totalCount === 'number' && totalCount >= 0) return totalCount;
    if (Array.isArray(inquiries)) return inquiries.length;
    return 0;
  };

  const totalInquiriesCount = countInquiries();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {alertMessage && (
        <div className="fixed top-4 right-4 z-50">
          <MessageAlert type={alertMessage.type} title={alertMessage.title} message={alertMessage.message} onClose={() => setAlertMessage(null)} />
        </div>
      )}

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY || '#05445E' }}>
            {strings.page?.header?.title || 'Inquiries'}
          </h1>
          <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY || '#189AB4' }}>
            {strings.page?.header?.subtitle || 'Manage customer inquiries and quotes'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div
            className="flex items-center gap-3 rounded-2xl border px-4 py-3"
            style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.15),
                color: UI_COLORS.BUTTONS.PRIMARY.background,
              }}
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {strings.page?.stats?.total}
              </p>
              <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {totalInquiriesCount}
              </p>
            </div>
          </div>
          {hasSelection && (
            <div
              className="rounded-2xl border px-4 py-3"
              style={{
                borderColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.4),
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
              }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.STATUS.INFO }}>
                {strings.page?.stats?.selected}
              </p>
              <p className="text-xl font-bold" style={{ color: UI_COLORS.STATUS.INFO }}>
                {selectionCount}
              </p>
            </div>
          )}
        </div>
      </div>

      <InquiryActions
        searchValue={searchInputValue}
        onSearchChange={handleSearch}
        onAddInquiry={handleCreateInquiry}
        onRefresh={handleRefresh}
        onBulkDelete={handleBulkDelete}
        hasSelection={hasSelection}
        selectionCount={selectionCount}
        loading={actionLoading}
        refreshLoading={refreshLoading}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={handleFromDateChange}
        onToDateChange={handleToDateChange}
      />

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
        <InquiriesTable
          inquiries={filteredInquiries}
          loading={loading}
          selectedInquiries={selectedInquiries}
          onSelectInquiry={selectInquiry}
          onSelectAll={selectAllInquiries}
          onEditInquiry={handleEditInquiry}
          onDeleteInquiry={handleDeleteInquiry}
          onViewInquiry={handleViewInquiry}
          onConvertInquiry={handleConvertInquiry}
          onCloseInquiry={handleRejectInquiry}
          onApproveInquiry={handleApproveInquiry}
          onRejectInquiry={handleRejectInquiry}
          onSendQuoteNotification={sendQuoteNotification}
          viewLoading={viewLoading}
          statusLoading={statusLoading}
        />
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={goToPage}
        loading={loading}
        totalLabel="total inquiries"
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {showInquiryForm && (
        <InquiryForm
          isOpen={showInquiryForm}
          onClose={() => {
            setShowInquiryForm(false);
            setEditingInquiry(null);
            setEditingInquiryId(null);
            setIsConverting(false);
          }}
          onSubmit={handleFormSubmit}
          inquiry={editingInquiry}
          inquiryId={editingInquiryId}
          isConverting={isConverting}
          loading={actionLoading}
        />
      )}

      {showViewModal && (
        <InquiryDetails
          isOpen={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingInquiry(null); }}
          inquiry={viewingInquiry}
          loading={viewLoading}
        />
      )}

      {deleteConfirm && (
        <ConfirmDialog
          isOpen
          title={deleteConfirm.title}
          message={deleteConfirm.message}
          confirmLabel={deleteConfirm.confirmLabel}
          cancelLabel={deleteConfirm.cancelLabel}
          confirmVariant={deleteConfirm.confirmVariant}
          loading={actionLoading}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

    </div>
  );
};

export default InquiriesPage;
